'use client';

import { useState, useEffect, type FormEvent } from 'react';
import Link from 'next/link';

const SUBJECTS = [
  'General Question',
  'Report Issue',
  'Billing/Subscription',
  'Account Help',
  'Feedback',
  'Other',
] as const;

function generateCaptcha() {
  const a = Math.floor(Math.random() * 8) + 2;
  const b = Math.floor(Math.random() * 8) + 2;
  return { a, b };
}

export default function SupportPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captcha, setCaptcha] = useState<{ a: number; b: number } | null>(null);

  useEffect(() => {
    setCaptcha(generateCaptcha());
  }, []);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage('');

    const answer = parseInt(captchaInput, 10);
    if (!captcha || isNaN(answer) || answer !== captcha.a + captcha.b) {
      setErrorMessage('Incorrect answer. Please try again.');
      setCaptchaInput('');
      setCaptcha(generateCaptcha());
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch('/api/support/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          subject,
          message,
          captchaA: captcha.a,
          captchaB: captcha.b,
          captchaAnswer: answer,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Something went wrong.' }));
        throw new Error(data.error || `Error ${res.status}`);
      }

      setStatus('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong.');
      setStatus('error');
      setCaptchaInput('');
      setCaptcha(generateCaptcha());
    }
  }

  if (status === 'success') {
    return (
      <div style={containerStyle}>
        <Link href="/" style={backLinkStyle}>&larr; Back to Home</Link>
        <div style={successBoxStyle}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', margin: '0 0 12px' }}>
            Message Sent
          </h2>
          <p style={{ color: '#555', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
            Thank you for reaching out. We typically respond within 1-2 business days.
          </p>
        </div>
        <Link href="/" style={{ ...backLinkStyle, display: 'inline-block', marginTop: 24 }}>
          Return to Home
        </Link>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <Link href="/" style={backLinkStyle}>&larr; Back to Home</Link>
      <h1 style={{ fontSize: 28, fontWeight: 700, margin: '20px 0 8px', color: '#1a1a2e' }}>
        Contact Support
      </h1>
      <p style={{ color: '#888', fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
        Have a question or need help? Fill out the form below and we will get back to you within 1-2 business days.
      </p>

      <form onSubmit={handleSubmit}>
        {errorMessage && (
          <div style={errorBoxStyle}>{errorMessage}</div>
        )}

        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            className="form-input"
            type="text"
            required
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            className="form-input"
            type="email"
            required
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="form-group">
          <label htmlFor="subject">Subject</label>
          <select
            id="subject"
            className="form-input"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          >
            <option value="" disabled>Select a topic</option>
            {SUBJECTS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="message">Message</label>
          <textarea
            id="message"
            className="form-input"
            required
            minLength={10}
            maxLength={5000}
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe your issue or question..."
            style={{ resize: 'vertical' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="captcha">What is {captcha ? `${captcha.a} + ${captcha.b}` : '...'}?</label>
          <input
            id="captcha"
            className="form-input"
            type="number"
            required
            value={captchaInput}
            onChange={(e) => setCaptchaInput(e.target.value)}
            placeholder="Your answer"
            style={{ maxWidth: 160 }}
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={status === 'submitting'}
          style={{ marginTop: 8 }}
        >
          {status === 'submitting' ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '40px 24px',
  fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif',
  color: '#1a1a2e',
  lineHeight: 1.7,
};

const backLinkStyle: React.CSSProperties = {
  color: '#D4A017',
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 600,
};

const successBoxStyle: React.CSSProperties = {
  marginTop: 32,
  padding: 24,
  borderRadius: 12,
  border: '1.5px solid #22c55e',
  background: '#f0fdf4',
};

const errorBoxStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderRadius: 8,
  border: '1px solid #fca5a5',
  background: '#fef2f2',
  color: '#991b1b',
  fontSize: 14,
  marginBottom: 18,
};
